<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useElementSize, useEventListener } from '@vueuse/core'

type DragMode = 'window' | 'left' | 'right' | null

type Percent = 0 | 50 | 100

interface Props {
  title: string
  percent?: Percent
  min?: number
  max?: number
  minSpan?: number
  maxSpan?: number
  step?: number
}

const props = withDefaults(defineProps<Props>(), {
  percent: 50,
  min: -2,
  max: 2,
  minSpan: 1,
  maxSpan: 2,
  step: 5,
})

const range = defineModel<number>('range', { default: 1 })
const offset = defineModel<number>('offset', { default: -1 })

const end = ref(offset.value)
const start = ref(offset.value - range.value)

const dragMode = ref<DragMode>(null)
const trackRef = ref<HTMLElement | null>(null)

const { width } = useElementSize(trackRef)

let pointerStartX = 0
let startSnapshot = 0
let endSnapshot = 0

const markers = computed(() => {
  const result = [0]

  for (let v = -props.step; v >= props.min; v -= props.step) {
    result.unshift(v)
  }

  for (let v = props.step; v <= props.max; v += props.step) {
    result.push(v)
  }

  return result
})

const valueToPx = (value: number) => {
  const ratio = (value - props.min) / (props.max - props.min)
  return Math.round(ratio * width.value)
}

const markerPx = (value: number) => valueToPx(value)

const span = computed(() => end.value - start.value)

const limitedRange = computed(() => props.max - props.min)

const pxPerUnit = computed(() => (width.value > 0 ? width.value / limitedRange.value : 0))

const leftPx = computed(() => valueToPx(start.value))

const rightPx = computed(() => valueToPx(end.value))

const windowWidth = computed(() => rightPx.value - leftPx.value)

const beginDrag = (e: PointerEvent, mode: DragMode) => {
  dragMode.value = mode

  pointerStartX = e.clientX
  startSnapshot = start.value
  endSnapshot = end.value
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
}

const moveWindow = (delta: number) => {
  const currentSpan = endSnapshot - startSnapshot

  let nextStart = startSnapshot + delta

  let nextEnd = endSnapshot + delta

  if (nextStart < props.min) {
    nextStart = props.min
    nextEnd = nextStart + currentSpan
  }

  if (nextEnd > props.max) {
    nextEnd = props.max
    nextStart = nextEnd - currentSpan
  }

  return {
    start: nextStart,
    end: nextEnd,
  }
}

const resizeLeft = (nextStart: number) => {
  const fixedEnd = endSnapshot

  let s = Math.round(nextStart)

  const minStart = fixedEnd - props.maxSpan
  const maxStart = fixedEnd - props.minSpan

  s = Math.max(s, minStart)
  s = Math.min(s, maxStart)
  s = Math.max(s, props.min)

  return {
    start: s,
    end: fixedEnd,
  }
}

const resizeRight = (nextEnd: number) => {
  const fixedStart = startSnapshot

  let e = Math.round(nextEnd)

  const minEnd = fixedStart + props.minSpan
  const maxEnd = fixedStart + props.maxSpan

  e = Math.max(e, minEnd)
  e = Math.min(e, maxEnd)
  e = Math.min(e, props.max)

  return {
    start: fixedStart,
    end: e,
  }
}

useEventListener(window, 'pointermove', (e: PointerEvent) => {
  if (!dragMode.value || pxPerUnit.value === 0) return

  const delta = Math.round((e.clientX - pointerStartX) / pxPerUnit.value)

  if (dragMode.value === 'window') {
    const result = moveWindow(delta)

    start.value = result.start
    end.value = result.end

    return
  }

  if (dragMode.value === 'left') {
    const result = resizeLeft(startSnapshot + delta)

    start.value = result.start
    end.value = result.end

    return
  }

  if (dragMode.value === 'right') {
    const result = resizeRight(endSnapshot + delta)

    start.value = result.start
    end.value = result.end

    return
  }
})

useEventListener(window, 'pointerup', () => {
  dragMode.value = null
})

watch(
  [offset, range],
  () => {
    end.value = offset.value
    start.value = offset.value - range.value
  },
  { immediate: true },
)

watch([start, end], () => {
  if (end.value < start.value) {
    const temp = start.value
    start.value = end.value
    end.value = temp
  }

  offset.value = end.value
  range.value = end.value - start.value
})
</script>

<template>
  <div class="p-3 overflow-hidden">
    <div class="flex items-center w-full">
      <div class="font-medium text-xs text-nowrap text-gray-700">{{ title }}</div>
      <div class="border-t border-gray-200 w-full ms-2"></div>
      <div class="font-medium border border-gray-200 text-brand text-xs text-nowrap px-1.5 py-1">
        {{ $t('glossary.amountDay', { amount: span }, span > 1 ? 2 : 1) }}
      </div>
    </div>
    <div class="mx-4 relative">
      <div ref="trackRef" class="select-none relative h-18 overflow-visible">
        <div
          class="absolute top-10 h-3 w-full bg-linear-to-r"
          :class="
            percent === 100
              ? 'from-gray-100 to-gray-300'
              : percent === 50
                ? 'from-gray-100 via-gray-300 to-gray-100'
                : 'from-gray-300 to-gray-100'
          "
        ></div>
        <div
          v-for="marker in markers"
          :key="marker"
          class="absolute w-px"
          :class="percent === marker ? 'top-6 h-11 bg-gray-500' : 'top-7.5 h-8 bg-gray-300'"
          :style="{ left: `${markerPx(marker)}px` }"
        >
          <div class="absolute -bottom-6 text-xs -translate-x-1/2">
            <span v-if="marker === 0" class="font-medium text-gray-700">
              {{ $t('glossary.today') }}
            </span>
            <span v-else class="text-gray-500">{{ marker }}</span>
          </div>
        </div>
        <div
          class="absolute top-9 h-5 cursor-grab bg-linear-to-r from-brand via-brand/80 to-brand shadow-md active:cursor-grabbing"
          :style="{
            left: `${leftPx}px`,
            width: `${windowWidth}px`,
          }"
          @pointerdown.stop="beginDrag($event, 'window')"
        >
          <div
            class="absolute inset-y-0 -top-1 -left-1.5 h-7 w-3 cursor-e-resize shadow"
            @pointerdown.stop="beginDrag($event, 'left')"
          >
            <div
              class="absolute -top-5 right-1.5 font-medium text-nowrap text-[11px] text-gray-700"
            >
              {{ start }}d
            </div>
            <div
              class="absolute left-1/2 top-1/2 h-full w-3 -translate-x-1/2 -translate-y-1/2 border border-gray-400 active:border-gray-700/80 hover:bg-gray-100 bg-white shadow-md"
            ></div>
          </div>
          <div
            class="absolute inset-y-0 -top-1 -right-1.5 h-7 w-3 cursor-ew-resize shadow"
            @pointerdown.stop="beginDrag($event, 'right')"
          >
            <div
              class="absolute -top-5 -right-1.5 font-medium text-nowrap text-[11px] text-gray-700"
            >
              {{ end }}d
            </div>
            <div
              class="absolute left-1/2 top-1/2 h-full w-3 -translate-x-1/2 -translate-y-1/2 border border-gray-400 active:border-gray-700/80 hover:bg-gray-100 bg-white shadow-md"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
